import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "contact-selection" }
const _hoisted_2 = {
  key: 0,
  class: "cap-warning"
}
const _hoisted_3 = { class: "contact-cell" }
const _hoisted_4 = { class: "avatar" }
const _hoisted_5 = ["src", "alt"]
const _hoisted_6 = {
  key: 1,
  class: "avatar-placeholder"
}
const _hoisted_7 = { class: "contact-info" }
const _hoisted_8 = { class: "contact-name" }
const _hoisted_9 = {
  key: 0,
  class: "contact-headline"
}
const _hoisted_10 = { class: "location" }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["href"]
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "pagination" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = { class: "page-indicator" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "per-page" }
const _hoisted_20 = { class: "per-page-select" }

import { computed } from "vue";
import { Badge, Icon, DataTable, Select } from "@/components/ui";
import type { TableColumn, PaginationState } from "@/components/ui/table/types";
import type { LinkedInSearchProspect } from "@/dtos/Unipile.dto";

interface Props {
  contacts: LinkedInSearchProspect[];
  selectedIds: string[];
  totalCount: number | null;
  totalItems: number;
  currentPage: number;
  perPage: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  loading: boolean;
  capReached: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactSelectionStep',
  props: {
    contacts: {},
    selectedIds: {},
    totalCount: {},
    totalItems: {},
    currentPage: {},
    perPage: {},
    canGoNext: { type: Boolean },
    canGoPrev: { type: Boolean },
    loading: { type: Boolean },
    capReached: { type: Boolean }
  },
  emits: ["update:selectedIds", "update:perPage", "prev-page", "next-page"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const PER_PAGE_OPTIONS = [50, 100, 200];

const perPageOptions = computed(() =>
  PER_PAGE_OPTIONS.map((opt) => ({ value: String(opt), label: String(opt) }))
);

const visibleContacts = computed(() =>
  props.contacts.map((contact) => ({
    ...contact,
    id: contact.id || contact.member_urn || "",
  }))
);

const paginationInfo = computed<PaginationState>(() => ({
  page: props.currentPage + 1,
  perPage: props.perPage,
  total:
    typeof props.totalCount === "number" && props.totalCount > 0
      ? props.totalCount
      : props.totalItems,
}));

const estimatedTotalPages = computed(() => {
  if (typeof props.totalCount !== "number" || props.perPage <= 0) return 0;
  return Math.max(1, Math.ceil(props.totalCount / props.perPage));
});

const columns = computed<TableColumn[]>(() => [
  { key: "name", header: "Contact", width: "35%" },
  { key: "location", header: "Location", width: "25%" },
  { key: "network_distance", header: "Connection", width: "15%" },
  { key: "profile", header: "Profile", width: "20%" },
]);

const handleSelectionChange = (ids: (string | number)[]) => {
  const visibleSet = new Set(visibleContacts.value.map((c) => String(c.id)));
  const incoming = new Set(ids.map(String));
  const next = new Set<string>();
  // Preserve selections from other pages
  for (const id of props.selectedIds) {
    if (!visibleSet.has(id)) next.add(id);
  }
  // Apply current page selections
  for (const id of incoming) next.add(id);
  emit("update:selectedIds", Array.from(next));
};

const onPerPageChange = (val: string | number) => {
  const num = Number(val);
  if (!Number.isFinite(num) || num <= 0) return;
  emit("update:perPage", num);
};

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const formatConnectionDegree = (distance: string): string => {
  const distanceMap: Record<string, string> = {
    FIRST_DEGREE: "1st",
    SECOND_DEGREE: "2nd",
    THIRD_DEGREE: "3rd",
    OUT_OF_NETWORK: "3rd+",
    DISTANCE_1: "1st",
    DISTANCE_2: "2nd",
    DISTANCE_3: "3rd",
    DISTANCE_4: "3rd+",
  };
  if (distanceMap[distance]) return distanceMap[distance];
  const match = distance.match(/(\d+)/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (num === 1) return "1st";
    if (num === 2) return "2nd";
    if (num === 3) return "3rd";
    return `${num}th`;
  }
  return distance;
};

const getConnectionVariant = (distance: string): "primary" | "secondary" | "info" => {
  const variantMap: Record<string, "primary" | "secondary" | "info"> = {
    FIRST_DEGREE: "primary",
    SECOND_DEGREE: "secondary",
    THIRD_DEGREE: "info",
    OUT_OF_NETWORK: "info",
    DISTANCE_1: "primary",
    DISTANCE_2: "secondary",
    DISTANCE_3: "info",
  };
  if (variantMap[distance]) return variantMap[distance];
  const match = distance.match(/(\d+)/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (num === 1) return "primary";
    if (num === 2) return "secondary";
  }
  return "info";
};

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = "none";
  const parent = img.parentElement;
  if (parent) parent.classList.add("avatar-fallback");
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.capReached)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, " Showing the first " + _toDisplayString(__props.totalItems) + " prospects — refine the URL for more. ", 1))
      : _createCommentVNode("", true),
    _createVNode(_unref(DataTable), {
      class: "prospects-table",
      data: visibleContacts.value,
      columns: columns.value,
      selectable: true,
      loading: __props.loading,
      "initial-selected-ids": __props.selectedIds,
      "on-selection-change": handleSelectionChange,
      pagination: paginationInfo.value,
      "show-pagination-info": true,
      "selected-count": __props.selectedIds.length,
      "empty-state": {
        title: 'No prospects on this page',
        description: 'Try adjusting your search URL or load the next page.',
        icon: 'mdi:account-search-outline',
      }
    }, {
      "cell(name)": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            (row.profile_picture_url)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: row.profile_picture_url,
                  alt: row.name,
                  class: "avatar-img",
                  onError: handleImageError
                }, null, 40, _hoisted_5))
              : (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(getInitials(row.name)), 1))
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", _hoisted_8, _toDisplayString(row.name), 1),
            (row.headline)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(row.headline), 1))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      "cell(location)": _withCtx(({ row }) => [
        _createElementVNode("span", _hoisted_10, _toDisplayString(row.location || "—"), 1)
      ]),
      "cell(network_distance)": _withCtx(({ row }) => [
        (row.network_distance)
          ? (_openBlock(), _createBlock(_unref(Badge), {
              key: 0,
              variant: getConnectionVariant(row.network_distance),
              size: "sm"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(formatConnectionDegree(row.network_distance)), 1)
              ]),
              _: 2
            }, 1032, ["variant"]))
          : (_openBlock(), _createElementBlock("span", _hoisted_11, "—"))
      ]),
      "cell(profile)": _withCtx(({ row }) => [
        (row.profile_url || row.public_profile_url)
          ? (_openBlock(), _createElementBlock("a", {
              key: 0,
              href: row.profile_url || row.public_profile_url,
              target: "_blank",
              rel: "noopener noreferrer",
              class: "profile-link",
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:linkedin",
                class: "linkedin-icon"
              }),
              _cache[3] || (_cache[3] = _createElementVNode("span", null, "View Profile", -1))
            ], 8, _hoisted_12))
          : (_openBlock(), _createElementBlock("span", _hoisted_13, "—"))
      ]),
      _: 1
    }, 8, ["data", "columns", "loading", "initial-selected-ids", "pagination", "selected-count"]),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("button", {
        type: "button",
        class: "page-btn",
        disabled: !__props.canGoPrev || __props.loading,
        onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('prev-page')))
      }, [
        _createVNode(_unref(Icon), {
          icon: "mdi:chevron-left",
          width: "16",
          height: "16"
        }),
        _cache[4] || (_cache[4] = _createTextVNode(" Prev ", -1))
      ], 8, _hoisted_15),
      _createElementVNode("span", _hoisted_16, [
        _createTextVNode(" Page " + _toDisplayString(__props.currentPage + 1), 1),
        (estimatedTotalPages.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_17, " of " + _toDisplayString(estimatedTotalPages.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("button", {
        type: "button",
        class: "page-btn",
        disabled: !__props.canGoNext || __props.loading,
        onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('next-page')))
      }, [
        _cache[5] || (_cache[5] = _createTextVNode(" Next ", -1)),
        _createVNode(_unref(Icon), {
          icon: "mdi:chevron-right",
          width: "16",
          height: "16"
        })
      ], 8, _hoisted_18),
      _createElementVNode("div", _hoisted_19, [
        _createElementVNode("div", _hoisted_20, [
          _createVNode(_unref(Select), {
            "model-value": String(__props.perPage),
            options: perPageOptions.value,
            size: "sm",
            "onUpdate:modelValue": onPerPageChange
          }, null, 8, ["model-value", "options"])
        ]),
        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "per-page-label" }, "per page", -1))
      ])
    ])
  ]))
}
}

})